import { computed, type IComputedValue } from 'mobx'
import { ORDER_ITEM_TYPE } from '@mixtiles/web-backend-shared'
import { experimentManager } from 'services/ExperimentManager/ExperimentManager'
import { serializePhotoBooksToCheckoutProducts } from 'services/PhotoBook/photoBookDTO'
import { PRODUCT_TYPES } from 'services/ProductTypeState'
import {
  readMobxSnapshot,
  readOutsideReaction,
} from 'utils/mobx/readOutsideReaction'
import type { PhotoBankStore } from 'stores/photoBankStore/PhotoBankStore'
import type { PhotoBookDataStore } from 'stores/photoBookDataStore/PhotoBookDataStore'
import type { PhotoBooksStore } from 'stores/photoBooksStore/PhotoBooksStore'
import type { PhotoBookCartItem } from '../../CartItem'
import type {
  CartItemsSyncResult,
  ReactiveCartItemAdapter,
} from '../CartItemAdapter.types'
import { buildPhotoBookCartItem } from './buildPhotoBookCartItem'

export function createPhotoBookCartItemAdapter({
  photoBooksStore,
  photoBankStore,
}: {
  photoBooksStore: PhotoBooksStore
  photoBankStore: PhotoBankStore
}): ReactiveCartItemAdapter<PhotoBookCartItem> {
  // One computed per book store, so a keystroke in one book rebuilds only that
  // book's item (a rebuild walks every photo of every page for warnings) while
  // the other books answer from cache.
  const itemComputeds = new WeakMap<
    PhotoBookDataStore,
    IComputedValue<PhotoBookCartItem | null>
  >()

  function getItemComputed(
    bookStore: PhotoBookDataStore
  ): IComputedValue<PhotoBookCartItem | null> {
    const existingComputed = itemComputeds.get(bookStore)
    if (existingComputed) return existingComputed

    const itemComputed = computed(() =>
      buildPhotoBookCartItem(bookStore.bookData, photoBankStore)
    )
    itemComputeds.set(bookStore, itemComputed)
    return itemComputed
  }

  return {
    kind: 'reactive',
    productType: PRODUCT_TYPES.PHOTO_BOOK,

    getCartItemsFromLoadedProduct(): CartItemsSyncResult<PhotoBookCartItem> {
      if (!photoBooksStore.isLoaded) return { status: 'not-loaded' }

      const items = photoBooksStore.bookStoresInDisplayOrder.flatMap(
        (bookStore) => {
          const item = getItemComputed(bookStore).get()
          return item ? [item] : []
        }
      )
      if (items.length === 0) return { status: 'not-existing' }
      return { status: 'existing', items }
    },

    forceProductLoad() {
      // The books' IndexedDB load starts when PhotoBooksStore is constructed,
      // so the product-sync reaction already sees them — nothing to force here.
    },

    hasBackingData(item) {
      return readOutsideReaction(() => {
        // While the books are loading, absence can't be proven — answering
        // false would prune every restored book row at boot. A row whose book
        // really is gone is pruned by the first sync after the load resolves.
        if (!photoBooksStore.isLoaded) return true

        // Undefined when no store was minted for this id, null when a store
        // exists but holds no book yet. Neither is a book this row can be
        // ordered from.
        const storedBook = photoBooksStore.booksMap.get(item.id)?.bookData
        return storedBook != null
      })
    },

    clearBackingData(item) {
      photoBooksStore.deleteBook(item.id)
    },

    buildPricingPayload(item) {
      return {
        productType: PRODUCT_TYPES.PHOTO_BOOK,
        products: serializePhotoBooksToCheckoutProducts({
          bookSize: item.metadata.size,
          pageCount: item.metadata.photosCount,
          quantity: item.quantity,
          isPriceTierEnabled: experimentManager.isEnabled(
            'pricing_photo_book_price_tier'
          ),
        }),
      }
    },

    buildFulfillmentPayload(item) {
      // Snapshotted: the cover and pages below go straight into the order
      // payload, which must carry plain JS rather than the live book.
      const bookData = readMobxSnapshot(
        () => photoBooksStore.booksMap.get(item.id)?.bookData
      )
      if (!bookData) return { orderTiles: [], orderItems: [] }

      return {
        orderTiles: [],
        orderItems: [
          {
            // The order endpoint prices each order product off this id, so it
            // carries the book's SKU size — not the book's own id.
            itemId: bookData.size,
            itemType: ORDER_ITEM_TYPE.PHOTO_BOOK,
            quantity: item.quantity,
            // The order's draft snapshot echoes this metadata beside the draft's
            // book map; without the id, a reorder reads the echo as a second book.
            metadata: {
              bookId: bookData.bookId,
              bookSize: bookData.size,
              cover: bookData.cover,
              pages: bookData.pages,
            },
          },
        ],
      }
    },
  }
}
